/**
 * Authentication store. Access token lives in memory only — never persisted to localStorage.
 * `isRestoring` is true while SilentRefresh attempts the initial /auth/refresh call on mount.
 */
import * as Sentry from '@sentry/react'
import { create } from 'zustand'
import type { AccountPrivate } from '@olimhub/shared'

interface AuthState {
  accessToken: string | null
  account: AccountPrivate | null
  isAuthenticated: boolean
  isRestoring: boolean
  setAuth: (token: string, account: AccountPrivate) => void
  updateAccount: (account: AccountPrivate) => void
  clearAuth: () => void
  setRestored: () => void
  hasCapability: (capability: string) => boolean
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  accessToken: null,
  account: null,
  isAuthenticated: false,
  isRestoring: true,

  setAuth: (token, account) => {
    Sentry.setUser({ id: account.id, username: account.username })
    set({ accessToken: token, account, isAuthenticated: true, isRestoring: false })
  },

  updateAccount: (account) => set({ account }),

  clearAuth: () => {
    Sentry.setUser(null)
    set({ accessToken: null, account: null, isAuthenticated: false, isRestoring: false })
  },

  setRestored: () => set({ isRestoring: false }),

  hasCapability: (capability: string): boolean => {
    const { account } = get()
    if (!account) return false
    return account.badges.some((ab) => {
      const caps = ab.badge.grantsCapabilities
      return caps.includes(capability) || caps.includes('super')
    })
  },
}))
